import React from 'react';
import { traitChip } from '../../contracts/scanFeedbackApi';

// One column of trait chips — STRONGEST FEATURES or AREAS TO WORK ON.
//
// Controlled and deliberately dumb: the page owns both selections because the
// two columns constrain each other (a trait cannot be both), and that rule
// lives in the shared validator rather than in either column.
//
// A chip is disabled, not hidden, when it is taken by the other column. Hiding
// it would make the grid reflow under the user's finger mid-tap and lose the
// explanation for why the option vanished.
export default function TraitPicker({
  title,
  hint,
  traits,
  selected,
  disabledKeys = [],
  onToggle,
  max,
  testId,
}) {
  const atMax = selected.length >= max;

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-display uppercase text-[11px] tracking-[0.2em] text-bone-dim">
          {title}
        </span>
        <span className="font-display text-[11px] tracking-[0.16em] text-bone-dim tabular-nums">
          {selected.length} / {max}
        </span>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label={title}>
        {traits.map((trait) => {
          const isSelected = selected.includes(trait.key);
          const takenByOther = disabledKeys.includes(trait.key);
          // At the cap, unselected chips go inert — but already-selected ones
          // must stay live or there is no way to change your mind.
          const disabled = takenByOther || (atMax && !isSelected);

          return (
            <button
              key={trait.key}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              // Scoped to the column: the same trait appears in BOTH pickers,
              // so a bare label leaves a screen-reader user hearing
              // "SKIN CLARITY, checkbox" twice with no way to tell which is
              // which.
              aria-label={`${trait.label}, ${title}`}
              disabled={disabled}
              onClick={() => onToggle(trait.key)}
              className={[
                'bg-transparent rounded-full px-4 py-2 cursor-pointer',
                'font-display uppercase text-[11px] tracking-[0.16em]',
                'transition-all duration-200 ease-mogged',
                'disabled:cursor-default disabled:opacity-35',
                isSelected
                  ? 'border border-aurum text-aurum-bright bg-aurum-faint'
                  : 'border border-[rgba(255,255,255,0.22)] text-bone-dim hover:border-[rgba(255,255,255,0.5)] hover:text-bone',
              ].join(' ')}
            >
              {traitChip(trait.key)}
            </button>
          );
        })}
      </div>

      {hint && (
        <p className="font-display italic text-bone-dim text-xs">{hint}</p>
      )}
    </div>
  );
}
